
/* eslint-disable no-mixed-operators */

import { get as apiGet } from "src/services/api";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";

import { BUILD_STATUSES, BRANCH_NAMES } from "@consts";
import { queryClient } from "src/lib/react-query/queryClient";
import { FETCH_QUERY_KEYS } from "src/queries/consts";

const PREVIEW_STATES = {
  DRAFT: "previewDraft",
  LIVE: "previewLive"
};

class BranchDeployerError extends Error {
  constructor(message) {
    super(message);
    this.name = "Branch deployer build failed";
  }
}

const createPreviewSlice = (set, get) => ({
  [PREVIEW_STATES.DRAFT]: {
    isStatusPollingInProgress: false,
    lastBuildAbort: null,
    hasFailedPolling: false,
    disablePublish: false,
    buildResponse: null
  },
  [PREVIEW_STATES.LIVE]: {
    isStatusPollingInProgress: false,
    lastBuildAbort: null,
    hasFailedPolling: false,
    buildResponse: null
  },
  setBuildDraftResponse: data => set({ [PREVIEW_STATES.DRAFT]: { ...get()[PREVIEW_STATES.DRAFT], buildResponse: data } }),
  updateDisablePublish: bool => set({ [PREVIEW_STATES.DRAFT]: { ...get()[PREVIEW_STATES.DRAFT], disablePublish: bool } }),
  pollPreviewStatus: async ({ isLivePreview = false, releaseName, branchName: branchNameParam, neverStopPolling = false } = {}) => {
    const stateKey = isLivePreview ? PREVIEW_STATES.LIVE : PREVIEW_STATES.DRAFT;
    const { selectedAssistant, draft } = get();

    set({ [stateKey]: { ...get()[stateKey], isStatusPollingInProgress: true, hasFailedPolling: false } });

    try {
      if (get()[stateKey].lastBuildAbort) {
        get()[stateKey].lastBuildAbort.abort();
      }
      const abort = new AbortController();
      set({ [stateKey]: { ...get()[stateKey], lastBuildAbort: abort } });

      const startPolling = async (isInitialPolling = false) => {
        const branchName = branchNameParam || (isLivePreview ? BRANCH_NAMES.MASTER : draft?.branchName);
        const params = new URLSearchParams();
        if (branchName) params.set("branchName", branchName);
        if (releaseName) params.set("releaseName", releaseName);
        const endpoint = `/assistants/${selectedAssistant.assistantId}/build/status?${params.toString()}`;
        const buildResponse = await apiGet({ endpoint, signal: abort.signal });

        set({ [stateKey]: { ...get()[stateKey], buildResponse } });

        if (buildResponse?.status === BUILD_STATUSES.LIVE ||
          buildResponse?.status === "" ||
          buildResponse?.status === BUILD_STATUSES.CHANGED) {
          if (!isInitialPolling) {
            queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.NOTIFICATIONS] });
            queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.PREVIEW_BRANCH_DEPLOYER] });
          }

          set({
            [stateKey]: {
              ...get()[stateKey],
              isStatusPollingInProgress: false,
              lastBuildAbort: null,
              hasFailedPolling: false,
              buildResponse
            }
          });

          if (!neverStopPolling) {
            return; // Stop polling as the desired status is reached
          }
        }

        if (buildResponse?.status === BUILD_STATUSES.FAILED) {
          if (!isInitialPolling) {
            queryClient.refetchQueries({ queryKey: [FETCH_QUERY_KEYS.NOTIFICATIONS] });
          }
          if (!neverStopPolling) {
            throw new BranchDeployerError();
          }
        }

        // Wait for 30 seconds before making the next request
        await new Promise(resolve => setTimeout(resolve, 30000));

        // Check if the assistant has changed during polling
        if (selectedAssistant.assistantId !== get().selectedAssistant?.assistantId) return;

        await startPolling(); // Recursively call the function again for the next poll
      };

      await startPolling(true);
    } catch (error) {
      if (error?.name !== "CanceledError") {
        if (!(error instanceof BranchDeployerError)) {
          console.error("Error occurred while polling preview status", error);
        }

        set({
          [stateKey]: {
            ...get()[stateKey],
            hasFailedPolling: true,
            isStatusPollingInProgress: false,
            buildResponse: { status: "" }
          }
        });
      }
    }
  },

  setIsAssistantPreviewable: () => {
    const {
      isSavingChanges,
      isDialogLoading,
      hasValidationErrors,
      systemMode
    } = get();
    const isAssistantPreviewable =
      (systemMode === SYSTEM_MODES.READ_ONLY || systemMode === SYSTEM_MODES.EDIT
        && !isSavingChanges
        && !isDialogLoading
        && !hasValidationErrors);
    set({ isAssistantPreviewable });
    return isAssistantPreviewable;
  },

  clearPreviewState: () => {
    set({
      [PREVIEW_STATES.DRAFT]: {
        isStatusPollingInProgress: false,
        lastBuildAbort: null,
        hasFailedPolling: false,
        disablePublish: false,
        buildResponse: null
      },
      [PREVIEW_STATES.LIVE]: {
        isStatusPollingInProgress: false,
        lastBuildAbort: null,
        hasFailedPolling: false,
        buildResponse: null
      }
    });
  }

});

export default createPreviewSlice;
